import {LAST_VIEW_CHANGED_WEB_VITALS} from '@aboutyou/logic-components';
import {Log, TadaridaWebVitals} from '@aboutyou/mlm';
import type {Metric} from 'web-vitals/attribution';

interface Attribution {
  element?: string;
  eventTarget?: string;
  largestShiftTarget?: string;
  closestElementDataTestId: string | null;
  elementDataTestId: string | null;
}

type MetricWithAttribution = Metric & {
  attribution: unknown;
};

interface Delta {
  lastRecordedTotalLayoutShift: number;
  eventParams: Record<string, unknown> | null;
}

const CLS_delta: Delta = {
  lastRecordedTotalLayoutShift: 0,
  eventParams: null,
};

const INP_delta: Delta = {
  lastRecordedTotalLayoutShift: 0,
  eventParams: null,
};

export const reportWebVitals = () => {
  import('./performance-logging').then(({webvitals}) => {
    const {onLCP, onFCP, onINP, onCLS, onTTFB} = webvitals;

    const addDataTestIdToEventParams = (
      eventParams: Record<string, unknown>,
    ) => {
      // different end save target under different property name
      const castEventAttribution = eventParams.attribution as Attribution;
      const availableSelector =
        castEventAttribution.element ||
        castEventAttribution.eventTarget ||
        castEventAttribution.largestShiftTarget;

      if (availableSelector) {
        try {
          // query selector will fail if selector has some special symbols in it
          // example: @ : /
          const element = document.querySelector(availableSelector);
          if (element) {
            const dataTestId = element.getAttribute('data-testid');
            if (!dataTestId) {
              const closestElement = element.closest('[data-testid]');
              if (closestElement) {
                (
                  eventParams.attribution as Attribution
                ).closestElementDataTestId =
                  closestElement.getAttribute('data-testid');
              } else {
                castEventAttribution.elementDataTestId = 'DataTestId NA';
              }
            } else {
              castEventAttribution.elementDataTestId = dataTestId;
            }
          }
        } catch (e) {
          castEventAttribution.closestElementDataTestId = `DataTestId - invalid selector: ${availableSelector}`;
        }
      }
    };

    const sendToLogs =
      (metricName: TadaridaWebVitals) =>
      ({name, delta, value, id, attribution}: MetricWithAttribution) => {
        const eventParams: Record<string, unknown> = {
          metric_name: name,
          metric_id: id,
          metric_value: value,
          metric_delta: delta,
          attribution,
        };

        addDataTestIdToEventParams(eventParams);

        switch (name) {
          case 'CLS':
            eventParams.metric_score = value;
            break;
        }

        Log.reportWebVitals(metricName, eventParams);
      };

    onFCP(sendToLogs('FCP'));
    onLCP(sendToLogs('LCP'));
    onTTFB(sendToLogs('TTFB'));
    onINP(sendToLogs('INP'), {
      reportAllChanges: false, // do not report the delta for each change but only the total for the session
    });
    onCLS(sendToLogs('CLS-session'), {
      reportAllChanges: false, // do not report the delta for each change but only the total for the session
    });

    const storeDelta =
      (stored: Delta, metricName: string) =>
      ({name, delta, value, id, attribution}: MetricWithAttribution) => {
        stored.eventParams = {
          metric_name: name,
          metric_id: id,
          metric_delta: delta,
          metric_value: value,
          metric_score: value,
          attribution,
        };
        addDataTestIdToEventParams(stored.eventParams);
        if (process.env.NODE_ENV === 'development') {
          /* eslint-disable no-console */
          console.groupCollapsed(
            `🟡 WebVitals: ${metricName}-delta-chunk:`,
            delta,
          );
          console.info(CLS_delta.eventParams);
          console.groupEnd();
          /* eslint-enable no-console */
        }
      };

    onCLS(storeDelta(CLS_delta, 'CLS'), {
      reportAllChanges: true, // for the delta for each change
    });
    onINP(storeDelta(INP_delta, 'INP'), {
      reportAllChanges: true, // for the delta for each change
    });
  });
};

/**
 * We report the accumulated CLS/INP delta on a page (url) when the page changes.
 * Reset the accumulated CLS/INP delta when the page changes, so that next view change can be reported accurately.
 * NOTE: The sum of all (CLS/INP)-delta's reported by the pages should be equal to the reported Session (CLS/INP)
 */
const reportDelta =
  (storedDelta: Delta, metricName: 'CLS' | 'INP') => (event: Event) => {
    const {lastPageType, newPageType} = (
      event as CustomEvent<{lastPageType: string; newPageType: string}>
    ).detail;
    if (storedDelta.eventParams) {
      const totalDeltaForLastView =
        (storedDelta.eventParams.metric_score as number) -
        storedDelta.lastRecordedTotalLayoutShift;

      if (totalDeltaForLastView) {
        Log.reportWebVitals(`${metricName}-delta`, {
          ...storedDelta.eventParams,
          metric_score: totalDeltaForLastView,
          metric_value: totalDeltaForLastView,
          lastPageType,
          newPageType,
        });
      }

      storedDelta.lastRecordedTotalLayoutShift = storedDelta.eventParams
        .metric_score as number;

      storedDelta.eventParams = null;
    }
  };

// eslint-disable-next-line ssr-friendly/no-dom-globals-in-module-scope
if (process.browser) {
  window.addEventListener(
    LAST_VIEW_CHANGED_WEB_VITALS,
    reportDelta(CLS_delta, 'CLS'),
  );

  window.addEventListener(
    LAST_VIEW_CHANGED_WEB_VITALS,
    reportDelta(INP_delta, 'INP'),
  );
}
